import path from "ramda/es/path";
import compose from "ramda/es/compose";
import converge from "ramda/es/converge";
import curry from "ramda/es/curry";
import map from "ramda/es/map";
import prop from "ramda/es/prop";
import zipObj from "ramda/es/zipObj";
import { useEffect, useMemo, useState } from 'react';
import { combineLatest } from 'rxjs';
import { first, map as rxMap } from 'rxjs/operators';
import { getFieldDescriptorWithInvariant, skipWhenStateEqual } from '../../../stream/utils';
import { createFieldRecord, getDeepPath } from '../../../utils/data';
import useContextOrStorage from '../../stream/useContextOrStorage';
const getFormValueForField = curry((fieldName, fieldIndices, [formData, formDescriptor])=>{
    const fieldDescriptor = getFieldDescriptorWithInvariant(formDescriptor, fieldName);
    return path(getDeepPath(formDescriptor, fieldDescriptor, fieldIndices), formData);
});
const mapToFieldValue = compose(rxMap, getFormValueForField);
/**
 * Hook that will provide form field value. Always use memoized input !!
 *
 * @documented
 * @category State
 * @import import useFormFieldValue from '@gef-ui/form-core/hooks/state/useFormFieldValue'
 * @param {Object} input
 * @param {String} input.fieldName
 * @param {Number[]} [input.fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {*} [initialData=null] - initial field data for useState.
 * @param {String} [formName] - when used, formStream$ is taken from stream storage instead of context
 * @returns {*} form field value
 * @example
 *
 * 	const fieldValue = useFormFieldValue({ fieldName: 'foo', fieldIndices: [1] }, 'defaultValue');
 */ const useFormFieldValue = ({ fieldName, fieldIndices: unsafeIndices }, initialData, formName)=>{
    const [data, setData] = useState(initialData);
    const { formData$, formDescriptor$ } = useContextOrStorage(formName);
    // biome-ignore lint/correctness/useExhaustiveDependencies: expand
    const fieldIndices = useMemo(()=>unsafeIndices, unsafeIndices);
    useEffect(()=>{
        if (formDescriptor$ && formData$) {
            const formDataAndDescriptor$ = combineLatest(formData$, formDescriptor$);
            formDataAndDescriptor$.pipe(mapToFieldValue(fieldName, fieldIndices), first()).subscribe(setData);
            const sub = formDataAndDescriptor$.pipe(skipWhenStateEqual(), mapToFieldValue(fieldName, fieldIndices)).subscribe(setData);
            return ()=>{
                sub.unsubscribe();
            };
        }
    }, [
        fieldName,
        fieldIndices,
        formData$,
        formDescriptor$
    ]);
    return data;
};
export const fieldNameP = prop('fieldName');
export const fieldIndicesP = prop('fieldIndices');
const createFieldRecords = map(converge(createFieldRecord, [
    fieldNameP,
    fieldIndicesP
]));
const mapToFieldValues = (fields)=>rxMap((formDataAndDescriptor)=>{
        const fieldRecords = createFieldRecords(fields);
        const fieldValues = fields.map(({ fieldName, fieldIndices })=>getFormValueForField(fieldName, fieldIndices, formDataAndDescriptor));
        return zipObj(fieldRecords, fieldValues);
    });
/**
 * Hook that will provide form field values in form of fieldRecord object. Always use memoized input !!
 *
 * @documented
 * @category State
 * @import import { useFormFieldValues } from '@gef-ui/form-core/hooks/state/useFormFieldValue'
 * @param {Object[]} fields
 * @param {String} [fields.fieldName]
 * @param {String} [fields.fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {*} [initialData={}] - initial fields data for useState.
 * @param {String} [formName] - when used, formStream$ is taken from stream storage instead of context
 * @returns {Object} form field values zipped to object by fieldRecord
 * @example
 *
 * 	const { name, car0 } = useFormFieldValue([ { fieldName: 'name' }, { fieldName: 'car', fieldIndices: [0] } ]);
 */ export const useFormFieldValues = (fields, initialData = {}, formName)=>{
    const [data, setData] = useState(initialData);
    const { formData$, formDescriptor$ } = useContextOrStorage(formName);
    useEffect(()=>{
        if (formDescriptor$ && formData$) {
            const formDataAndDescriptor$ = combineLatest(formData$, formDescriptor$);
            formDataAndDescriptor$.pipe(mapToFieldValues(fields), first()).subscribe(setData);
            const sub = formDataAndDescriptor$.pipe(skipWhenStateEqual(), mapToFieldValues(fields)).subscribe(setData);
            return ()=>{
                sub.unsubscribe();
            };
        }
    }, [
        fields,
        formData$,
        formDescriptor$
    ]);
    return data;
};
export default useFormFieldValue;
